import { AccountReauthBanner } from "@/components/AccountReauthBanner.tsx";
import { AttributionCapture } from "@/components/AttributionCapture.tsx";
import { GlobalAuthErrorHandler } from "@/components/GlobalAuthErrorHandler.tsx";
import { OkthxLogo } from "@/components/icons/Logo.tsx";
import { useProvideScrollbarWidth } from "@/components/layout/scrollbarWidth.tsx";
import { TimezoneSwitchPrompt } from "@/components/location/TimezoneSwitchPrompt.tsx";
import { UnitsExhaustedDialog } from "@/components/billing/UnitsExhaustedDialog.tsx";
import { Loader } from "@/components/loader/Loader.tsx";
import { LinkWallet } from "@/components/payments/LinkWallet.tsx";
import { PaymentPilot } from "@/components/payments/PaymentPilot.tsx";
import { ColorSchemeProvider } from "@/components/ui/color-scheme-provider.tsx";
import { Toaster } from "@/components/ui/toast.tsx";
import { consumePendingToast } from "@/lib/deferredToast.ts";
import { sanitizeStatsigAutoCaptureEvent } from "@/lib/statsigAutoCapture.ts";
import { buildStatsigOverrideAdapter } from "@/lib/statsigOverrides.ts";
import { OmniboxEnrichmentProvider } from "@/contexts/OmniboxEnrichmentContext.tsx";
import { SocketProvider } from "@/contexts/SocketContext.tsx";
import { useIncrementalSync } from "@/hooks/sync.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { NotificationProvider } from "@/hooks/useBrowserNotifications/NotificationProvider.tsx";
import { useServiceWorkerNavigation } from "@/hooks/useBrowserNotifications/useServiceWorkerNavigation.ts";
import { useCalendarEventsSocket } from "@/components/calendars/useCalendarEvents.ts";
import { useCalendarsChangedSocket } from "@/components/calendars/useCalendarsChangedSocket.ts";
import { useTextMessagesChangedSocket } from "@/components/connectors/textMessages/useTextMessagesChangedSocket.ts";
import { useConnectorOAuthResults } from "@/components/connectors/useConnectorOAuthResults.ts";
import { McpOAuthErrorDialog } from "@/components/connectors/McpOAuthErrorDialog.tsx";
import { SlackChannelPickerDialog } from "@/components/connectors/slack/SlackChannelPickerDialog.tsx";
import { takeConnectStarted } from "@/components/connectors/pendingConnect.ts";
import { TeamsInstallDialog } from "@/components/connectors/teams/TeamsInstallDialog.tsx";
import type { McpOAuthFailure } from "@/shared/connectors/mcpOAuthErrors.ts";
import { useTasksSocket } from "@/components/tasks/useTasks.ts";
import { useUserTasksSocket } from "@/components/userTasks/api.ts";
import { useThreadCanvasSocket } from "@/components/threads/useThreadCanvas.tsx";
import { usePresenceTracking } from "@/hooks/usePresenceTracking.ts";
import { useThreadMutatedSocket } from "@/hooks/useThreads.tsx";
import { useBadgeRefreshSocket } from "@/hooks/useBadgeRefreshSocket.tsx";
import { useThreadNotifyToast } from "@/hooks/useThreadNotifyToast.tsx";
import { useDocumentTitleBadge } from "@/hooks/useDocumentTitleBadge.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { resolveAuthGate } from "@/lib/authGate.ts";
import type { GetAccountContextResp } from "@/types/accounts.ts";
import { NATIVE_HTTPS_CALLBACK_PATH } from "@/types/nativeSignIn.ts";
import { Auth } from "@/views/Auth.tsx";
import { Artifact } from "@/views/Artifact.tsx";
import { PIVOT_ROUTE_PATTERN } from "@/components/layout/pivotRoutes.ts";
import Calendar from "@/views/Calendar.tsx";
import { CommunicationDetail } from "@/views/CommunicationDetail.tsx";
import { CommunicationThread } from "@/views/CommunicationThread.tsx";
import { SourceRedirect } from "@/views/SourceRedirect.tsx";
import { PeopleListScreen } from "@/views/PeopleList.tsx";
import { Download } from "@/views/Download.tsx";
import { Claim } from "@/views/Claim.tsx";
import { PhoneAuth } from "@/views/PhoneAuth.tsx";
import { WhatsAppAuth } from "@/views/WhatsAppAuth.tsx";
import { ReferralLanding } from "@/views/ReferralLanding.tsx";
import { Pricing } from "@/views/Pricing.tsx";
import { GrantRequired } from "@/views/GrantRequired.tsx";
import { NativeSignInCallback } from "@/views/NativeSignInCallback.tsx";
import { ScopeSelection } from "@/views/ScopeSelection.tsx";
import { MessageArtifactsScreen } from "@/views/MessageArtifacts.tsx";
import { MessagingConsent } from "@/views/MessagingConsent.tsx";
import { NotFound } from "@/views/NotFound.tsx";
import { OAuthResult } from "@/views/OAuthResult.tsx";
import { Onboarding } from "@/views/Onboarding/Onboarding.tsx";
import { Settings } from "@/views/Settings/Settings.tsx";
import { WebsiteSignInsPage } from "@/views/WebsiteSignIns.tsx";
import { ServiceUnavailable } from "@/views/ServiceUnavailable.tsx";
import { SharedArtifact } from "@/views/SharedArtifact.tsx";
import { SharedSnapshot } from "@/views/SharedSnapshot.tsx";
import { StartThread } from "@/views/StartThread.tsx";
import { ThreadParticipants } from "@/views/ThreadParticipants.tsx";
import { StatsigProvider, useClientAsyncInit } from "@statsig/react-bindings";
import { StatsigSessionReplayPlugin } from "@statsig/session-replay";
import { StatsigAutoCapturePlugin } from "@statsig/web-analytics";
import { QueryClientProvider } from "@tanstack/react-query";
import { QueryDevtools } from "@/components/layout/QueryDevtools.tsx";
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Redirect, Route, Router, Switch, useLocation } from "wouter";
import { GoToNavigation } from "./components/keyboard/GoToNavigation.tsx";
import { KeyboardShortcutsManager } from "./components/keyboard/KeyboardShortcutsOverlay.tsx";
import { useShortcut } from "./components/keyboard/useShortcut.ts";
import { MarkerIoProvider } from "./components/marker.io/MarkerIoContext.tsx";
import { ConnectorPromptModal } from "./components/connectors/ConnectorPromptModal.tsx";
import { PushPromptModal } from "./components/notifications/PushPromptModal.tsx";
import { NativeAppStateListener } from "./components/NativeAppStateListener.tsx";
import { NativeNavigationListener } from "./components/NativeNavigationListener.tsx";

/**
 * Rather than using the animated Loader, which visually "stutters" if it gets mounted repeatedly
 * during a React component loading waterfall, we use the static logo for the top level loading phases.
 * This allows the screens to use the actual Loader component, because we can transition from this
 * to the Loader component without it looking weird.
 *
 * We should revisit this and maybe just use a top level suspense, or at least a global suspense that
 * mounts this and a per-page suspense for the Loader (for internal page loader waterfalls).
 * TODO: https://linear.app/big-basin-labs/issue/BIG-682/fix-double-loader-stutter
 */
const AppSplash = () => {
  // In native WebView, the native skeleton handles loading state
  if (window.isWebView) return null;

  return (
    <div className="h-dvh flex items-center justify-center">
      <OkthxLogo className="size-12" viewBox="-0.118 0.45 23.593 23.593" />
    </div>
  );
};

const statsigOverrideAdapter = buildStatsigOverrideAdapter({
  dev: import.meta.env.DEV,
  gates: import.meta.env.VITE_STATSIG_GATE_OVERRIDES,
  dynamicConfigs: import.meta.env.VITE_STATSIG_DYNAMIC_CONFIG_OVERRIDES,
});

/** Initialize statsig with our credentials for a given user. */
const BBLStatsigProvider = ({
  children,
  userContext,
}: {
  children: React.ReactNode;
  userContext: GetAccountContextResp;
}) => {
  const { client } = useClientAsyncInit(
    import.meta.env.VITE_STATSIG_SDK_KEY, // https://vite.dev/guide/env-and-mode
    {
      userID: userContext.user.id.toString(),
      email: userContext.user.userPrimaryEmail ?? undefined,
      custom: {
        isEmployee: userContext.user.isEmployee,
        isAdmin: userContext.user.isAdmin ?? false,
        // A phone-only account has no email, so the verified phone is the
        // identity a rule can name it by. Statsig has no phone field of its own.
        phone: userContext.user.userPrimaryPhone ?? undefined,
      },
    },
    {
      plugins: [
        // Replays go to a third party, so they must never carry typed input or
        // on-screen text (CASA 6.5.1). Statsig's remote privacy configuration
        // overrides these masking fields. We trust Statsig admins to keep it on
        // Maximum with no unmasked selectors unless the exposure risk is handled
        // first; these options preserve strict masking without a remote override.
        new StatsigSessionReplayPlugin({
          rrwebConfig: { maskAllInputs: true, maskTextSelector: "*" },
        }),
        // Autocapture sends element text by default and has no equivalent to
        // replay's Maximum privacy mode, so strip content before it reaches Statsig.
        new StatsigAutoCapturePlugin({
          eventFilterFunc: sanitizeStatsigAutoCaptureEvent,
          consoleLogAutoCaptureSettings: {
            enabled: true,
            logLevel: "log",
            sampleRate: 1.0,
          },
        }),
      ],
      environment: { tier: import.meta.env.MODE },
      overrideAdapter: statsigOverrideAdapter,
    },
  );

  return (
    <StatsigProvider client={client} loadingComponent={<AppSplash />}>
      {children}
    </StatsigProvider>
  );
};

/**
 * Catch-all for app-wide listeners: socket hooks, which need to be mounted below
 * `SocketProvider`, plus the cross-tab OAuth result subscription.
 *
 * The OAuth subscription belongs at this level, not on a connector surface. A
 * reconnect leaves for the provider in a tab of its own and reports back over a
 * BroadcastChannel that only reaches a mounted subscriber, while the thing most
 * visibly stuck on the result — `AccountReauthBanner` — is mounted app-wide. A
 * subscription scoped to the connectors gallery misses the reply whenever the
 * user reconnects from the Account tab or navigates away mid-flow, which left
 * the banner insisting on a reconnect that had already happened (BIG-7329).
 *
 * `showToasts: false` because connector surfaces keep the existing non-MCP
 * toasts. MCP failures are delivered separately to the one dialog mounted
 * here, so surface subscriptions never announce them twice.
 */
const GlobalSockets = () => {
  const [mcpOAuthFailure, setMcpOAuthFailure] = useState<McpOAuthFailure | null>(null);
  const [slackPickerTeamId, setSlackPickerTeamId] = useState<string | null>(null);
  const onSlackConnected = useCallback(({ teamId }: { teamId: string }) => {
    if (takeConnectStarted("slack")) setSlackPickerTeamId(teamId);
  }, []);
  const closeSlackPicker = useCallback(() => setSlackPickerTeamId(null), []);
  const [teamsInstallOpen, setTeamsInstallOpen] = useState(false);
  const onTeamsConnected = useCallback(() => {
    if (takeConnectStarted("teams")) setTeamsInstallOpen(true);
  }, []);
  const closeTeamsInstall = useCallback(() => setTeamsInstallOpen(false), []);
  useThreadMutatedSocket();
  useTasksSocket();
  useUserTasksSocket();
  useThreadCanvasSocket();
  useCalendarEventsSocket();
  useCalendarsChangedSocket();
  useTextMessagesChangedSocket();
  useBadgeRefreshSocket();
  useThreadNotifyToast();
  useConnectorOAuthResults({
    showToasts: false,
    onMcpFailure: setMcpOAuthFailure,
    onSlackConnected,
    onTeamsConnected,
  });

  return (
    <>
      <McpOAuthErrorDialog failure={mcpOAuthFailure} onClose={() => setMcpOAuthFailure(null)} />
      {slackPickerTeamId && (
        <SlackChannelPickerDialog
          key={slackPickerTeamId}
          teamId={slackPickerTeamId}
          onClose={closeSlackPicker}
        />
      )}
      {teamsInstallOpen && <TeamsInstallDialog onClose={closeTeamsInstall} />}
    </>
  );
};

/** Catch-all for global effects that need to be mounted within the router. */
const GlobalEffects = () => {
  useServiceWorkerNavigation();
  return <GlobalAuthErrorHandler />;
};

/** Empty shell route for the persistent native WebView to load without fetching thread data. */
const ThreadShell = () => null;

/** Routes public pages before mounting authenticated hooks. */
const AppContent = () => {
  const [location] = useLocation();

  // Safe hooks that don't make authenticated requests
  useIsMobile();
  useProvideScrollbarWidth();

  // Handle pages that don't require auth before mounting AuthenticatedApp,
  // which would fire authenticated requests and trigger 401 redirects.

  switch (location) {
    case "/download":
      return <Download />;
    case "/messaging-consent":
      return <MessagingConsent />;
    case "/auth/oauth/result":
      return <OAuthResult />;
    case "/auth/grant-required":
      return <GrantRequired />;
    case "/auth/access":
      // Where the api parks the OAuth tab before Google's consent screen.
      // Handled here rather than inside Auth for the same reason
      // grant-required is: mounting AuthenticatedApp would fire authenticated
      // requests for a user who does not exist yet.
      return <ScopeSelection />;
    case NATIVE_HTTPS_CALLBACK_PATH:
      return <NativeSignInCallback />;
    case "/claim":
      return <Claim />;
    case "/pricing":
      // Public, and reachable only by URL for now: nothing links here yet.
      return <Pricing />;
    case "/auth/phone":
      // Public for the same reason the claim landing is: it runs before any
      // session exists, and mounting AuthenticatedApp would fire authenticated
      // requests for a user who does not exist yet.
      return <PhoneAuth />;
    case "/auth/whatsapp":
      // Public, like the phone door it finishes through.
      return <WhatsAppAuth />;
  }

  // A referral link: public, since its visitor has no account yet.
  const referralCode = location.match(/^\/r\/([^/]+)\/?$/)?.[1];
  if (referralCode) {
    return <ReferralLanding code={referralCode} />;
  }

  // Handle shared snapshots and shared artifacts (public, no auth required)
  // Maybe hacky but the message list currently requires a socket provider to be present, so we need to wrap it in one
  // even though we don't need it for the shared snapshot page.
  if (location.startsWith("/shared/")) {
    return (
      <SocketProvider userContext={null}>
        <Router>
          {/* Three segments, so it can't be swallowed by the two-segment
              snapshot route below whatever the order. */}
          <Route path="/shared/a/:shareToken" component={SharedArtifact} />
          <Route path="/shared/:shareToken" component={SharedSnapshot} />
        </Router>
      </SocketProvider>
    );
  }

  return <AuthenticatedApp />;
};

/** Mounts hooks that make authenticated requests (user context, presence, sync). */
const AuthenticatedApp = () => {
  const { userContext, isPending, fetchStatus, error, mutate } = useCurrentUserContext();
  const [shortcutsOverlayOpen, setShortcutsOverlayOpen] = useState(false);

  // Global hotkeys
  const { handleIncrementalSync } = useIncrementalSync();
  useShortcut("system.sync", handleIncrementalSync, { deps: [handleIncrementalSync] });

  // User presence tracking
  usePresenceTracking();

  // Awareness badge count in the browser tab title.
  useDocumentTitleBadge();

  // Replay any toast stashed by a pre-reload flow (e.g., account switch / add-account).
  // Fires once userContext has resolved so the toast lands on the fresh account's UI.
  const toastConsumedRef = useRef(false);
  useEffect(() => {
    if (!userContext || toastConsumedRef.current) return;
    toastConsumedRef.current = true;
    consumePendingToast();
  }, [userContext]);

  const authGate = resolveAuthGate({
    hasUser: userContext !== undefined,
    isPending,
    fetchStatus,
    error,
  });
  switch (authGate) {
    case "loading":
      return <AppSplash />;
    case "signed-out":
      return (
        <>
          <GlobalEffects />
          <Auth />
        </>
      );
    case "unreachable":
      return <ServiceUnavailable onRetry={() => void mutate()} />;
    case "app":
      break;
  }

  if (userContext === undefined) {
    throw new Error("Authenticated auth gate resolved without user context");
  }

  return (
    <BBLStatsigProvider userContext={userContext}>
      <MarkerIoProvider userContext={userContext}>
        <SocketProvider userContext={userContext}>
          <GlobalSockets />
          <ColorSchemeProvider colorScheme="light">
            <NotificationProvider>
              {/* Column constrained to the viewport so the reauth banner shrinks
                  the content area instead of stacking on top of a full-height
                  view and pushing the calendar's bottom floating buttons
                  off-screen on mobile. */}
              <div className="flex h-full flex-col">
                <AccountReauthBanner />
                <KeyboardShortcutsManager
                  open={shortcutsOverlayOpen}
                  setOpen={setShortcutsOverlayOpen}
                />
                <div className="min-h-0 flex-1">
                  <Router>
                    <GlobalEffects />
                    <TimezoneSwitchPrompt />
                    <UnitsExhaustedDialog />
                    <ConnectorPromptModal />
                    <PushPromptModal />
                    <GoToNavigation />
                    <NativeAppStateListener />
                    <NativeNavigationListener />
                    <OmniboxEnrichmentProvider>
                      <Suspense fallback={<Loader className="h-full" />}>
                        <Switch>
                          {/* Every sidebar destination is its own address, so
                              which canvas the app opens on is something the URL
                              says rather than a mode a query param puts home
                              into. `/` is among them — it is New chat, the app's
                              home.

                              One route matching all of them, not a route each:
                              the Switch renders the child that matches and React
                              reconciles it by identity, so a route per path
                              would remount the whole canvas on every move
                              between destinations. The pattern is built from the
                              destination table, which is a `Record<Pivot, …>` —
                              a destination cannot join the sidebar without a
                              path, and this cannot fall behind it. */}
                          <Route component={Calendar} path={PIVOT_ROUTE_PATTERN} />
                          <Route component={Auth} path="/auth" />
                          <Route component={LinkWallet} path="/payments/link" />
                          <Route component={PaymentPilot} path="/payments/pilot/:attemptId" />
                          <Route component={Onboarding} path="/onboarding/:step?" />
                          {/* Memories now live under Settings; redirect the
                              legacy top-level route. */}
                          <Route path="/memories">
                            <Redirect to="/settings/memories" replace />
                          </Route>
                          <Route component={ThreadShell} path="/threads/__shell" />
                          {/* The canvas route above claims the bare
                              `/threads/:id` — a thread is one of the
                              destinations that shell renders, so it keeps the
                              list beside it and survives the move like any
                              other. These two are their own screens, and its
                              pattern is anchored so neither is swallowed. */}
                          <Route
                            component={ThreadParticipants}
                            path="/threads/:threadId/participants"
                          />
                          {/* Type-qualified artifact URL (`/artifacts/emailDraft/7`)
                              round-trips to the canonical URN; the bare-id route
                              stays for already-shared links. Both render `Artifact`. */}
                          <Route component={Artifact} path="/artifacts/:artifactType/:artifactId" />
                          <Route component={Artifact} path="/artifacts/:artifactId" />
                          <Route component={StartThread} path="/start-thread" />
                          <Route
                            component={MessageArtifactsScreen}
                            path="/threads/:threadId/messages/:messageId/tasks"
                          />
                          {/* Routines & Skills live under Settings; the nested
                              route carries the `:subId` (routine id / skill name
                              / "new") segment the active flow reads. */}
                          <Route component={Settings} path="/settings/:tab/:subId" />
                          <Route component={Settings} path="/settings/:tab?" />
                          <Route component={WebsiteSignInsPage} path="/website-sign-ins" />
                          {/* The conversation a message belongs to, and the
                              message itself. Anchored patterns, so the
                              two-segment form cannot be swallowed by the one
                              below it. */}
                          <Route
                            component={CommunicationThread}
                            path="/communications/:id/thread"
                          />
                          <Route component={CommunicationDetail} path="/communications/:id" />
                          <Route component={SourceRedirect} path="/sources/:id" />
                          <Route component={PeopleListScreen} path="/people" />
                          {/* `/contacts/:id`, `/routines/:id` and
                              `/skills/:name` are the canvas's, claimed by the
                              pivot pattern at the top of this Switch so
                              drilling into one does not remount the column.
                              `/skills/new` is the exception the pattern leaves
                              out: the create form is the settings screen's. */}
                          <Route path="/skills/new">
                            <Redirect to="/settings/skills/new" replace />
                          </Route>
                          <Route component={NotFound} path="*" />
                        </Switch>
                      </Suspense>
                    </OmniboxEnrichmentProvider>
                  </Router>
                </div>
              </div>
            </NotificationProvider>
          </ColorSchemeProvider>
        </SocketProvider>
      </MarkerIoProvider>
    </BBLStatsigProvider>
  );
};

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AttributionCapture />
      <AppContent />
      <Toaster position="top-center" />
      <QueryDevtools />
    </QueryClientProvider>
  );
};
